import { Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompareJobsButton } from '@/console/features/jobs/components/compare-jobs-button/compare-jobs-button';
import { JobDetails } from '@/console/features/jobs/components/job-details/job-details';
import { JobDifferences } from '@/console/features/jobs/components/job-differences/job-differences';
import { JobLogs } from '@/console/features/jobs/components/job-logs/job-logs';
import { JobLogsTitleContainer } from '@/console/features/jobs/components/job-logs-drawer/job-logs-drawer.styled';
import { TJobLogsDrawerProps } from '@/console/features/jobs/components/job-logs-drawer/job-logs-drawer.types';
import { usePipelineStepType } from '@/console/features/pipelines/hooks/use-pipeline-step-type/use-pipeline-step-type';
import { AlertIcon } from '@/shared/assets/icons/alert-icon';
import { CompareIcon } from '@/shared/assets/icons/compare-icon';
import { InfoIcon } from '@/shared/assets/icons/info-icon';
import { LogsIcon } from '@/shared/assets/icons/logs-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { Chip } from '@/shared/components/ui/chip/chip';
import { CopyIdButton } from '@/shared/components/ui/copy-id-button/copy-id-button';
import { LoaderModal } from '@/shared/components/ui/loader-modal/loader-modal';
import { LoadingDots } from '@/shared/components/ui/loading-dots/loading-dots';
import { MultiDrawer } from '@/shared/components/ui/multi-drawer/multi-drawer';
import { TMultiDrawerTab } from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { getStuckReasonTranslationKey } from '@/shared/features/jobs/jobs.utils';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadJobQuery } from '@/shared/redux/api/jobs/jobs-api';
import { ReadJobQuery } from '@/shared/types/types.generated';

const JOB_STATUS_POLLING_INTERVAL_MS = 20_000;

export const JobLogsDrawer: React.FC<TJobLogsDrawerProps> = ({
  jobId,
  job: injectedJob,
  trigger,
  action,
  defaultTab,
  deferJobFetchUntilOpen = false,
}) => {
  const { t } = useTranslation();
  const { getStatusColor, getStatusLabel, isJobRunning } = useJobStatus();
  const { getStepTypeIcon, getStepTypeLabel } = usePipelineStepType();
  const [isLoadingComparison, setIsLoadingComparison] =
    useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const shouldStopPolling = (data: ReadJobQuery) => {
    return !data?.readJob || !isJobRunning(data.readJob?.status);
  };

  const { data, isLoading, isFetching } = usePollingQuery({
    useQueryHook: useReadJobQuery,
    queryArg: { jobId: jobId! },
    shouldStopPolling,
    // Skip the per-job fetch when the caller already supplied the job (kept live by
    // the caller's own batched poll), or while explicitly deferred until opened.
    queryOptions: {
      skip:
        (deferJobFetchUntilOpen && !isDrawerOpen) || !jobId || !!injectedJob,
    },
    pollingInterval: JOB_STATUS_POLLING_INTERVAL_MS,
  });

  const job = injectedJob ?? data?.readJob;
  const showJobLoadingState = isDrawerOpen && !job && (isLoading || isFetching);
  // Backward compatibility: support both differences_md and differences_target_md
  const differencesTarget =
    job?.metaData?.differences_target_md || job?.metaData?.differences_md;
  const differencesPrevious = job?.metaData?.differences_previous_md;
  const errorMessage = useMemo(() => {
    const raw = job?.errorMessage;
    const stuckReasonKey = getStuckReasonTranslationKey(raw);
    return stuckReasonKey ? t(stuckReasonKey) : raw;
  }, [job?.errorMessage, t]);
  const editorHeight = 'calc(100vh - 12.5rem)';

  const drawerTitle = (closeDrawer: () => void) => {
    if (!job) {
      return (
        <Stack direction="row" alignItems="center" gap="1rem">
          <Typography variant="h5">{t('pipelines:job_logs')}</Typography>
          {action}
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" gap="1rem">
        <JobLogsTitleContainer>
          {getStepTypeIcon(job.jobType)}
          <Typography variant="h5">
            {getStepTypeLabel(job.jobType)} {t('common:logs')}
          </Typography>
        </JobLogsTitleContainer>
        <Chip
          withDot
          status={getStatusColor(job.status)}
          label={getStatusLabel(job.status)}
          tooltip={errorMessage}
        />
        {action}
        <CompareJobsButton
          job={job}
          onCallback={() => {
            closeDrawer();
            setIsLoadingComparison(true);
          }}
        />
      </Stack>
    );
  };

  const errorTab: TMultiDrawerTab = {
    title: t('common:error'),
    icon: <AlertIcon />,
    disabled: !errorMessage,
    content: () => <Alert color="red" text={errorMessage || ''} />,
  };

  const differencesTargetTab: TMultiDrawerTab = {
    title: t(
      differencesPrevious
        ? 'pipelines:differences_from_target'
        : 'common:differences',
    ),
    icon: <CompareIcon />,
    disabled: !differencesTarget,
    content: () => (
      <JobDifferences
        differences={differencesTarget}
        editorHeight={editorHeight}
      />
    ),
  };

  const differencesPreviousTab: TMultiDrawerTab = {
    title: t('pipelines:differences_from_previous'),
    icon: <CompareIcon />,
    disabled: !differencesPrevious,
    content: () => (
      <JobDifferences
        differences={differencesPrevious}
        editorHeight={editorHeight}
      />
    ),
  };

  const jobLogsTab: TMultiDrawerTab = {
    title: t('common:logs'),
    icon: <LogsIcon />,
    content: () => (
      <JobLogs
        jobId={jobId}
        jobStatus={job?.status}
        editorHeight={editorHeight}
      />
    ),
  };

  const jobDetailsTab: TMultiDrawerTab = {
    title: t('common:details'),
    icon: <InfoIcon />,
    content: () =>
      showJobLoadingState ? (
        <Stack alignItems="center" padding="2rem">
          <LoadingDots />
        </Stack>
      ) : (
        <JobDetails job={job} />
      ),
  };

  const tabs: TMultiDrawerTab[] = [
    ...(errorMessage ? [errorTab] : []),
    ...(differencesTarget ? [differencesTargetTab] : []),
    ...(differencesPrevious ? [differencesPreviousTab] : []),
    jobLogsTab,
    jobDetailsTab,
  ];

  const getDefaultTab = () => {
    if (defaultTab === 'target') {
      return Math.max(0, tabs.indexOf(differencesTargetTab));
    }
    if (defaultTab === 'previous') {
      return Math.max(0, tabs.indexOf(differencesPreviousTab));
    }
    if (typeof defaultTab === 'number') {
      return defaultTab;
    }
    return 0;
  };

  return (
    <>
      <LoaderModal
        open={isLoadingComparison}
        loaderText={t('projects:preparing_comparison')}
        headline={t('common:loading')}
      />
      <MultiDrawer
        title={drawerTitle}
        headerActions={
          <CopyIdButton
            id={job?.jobId ?? jobId ?? ''}
            buttonSize="extraSmall"
            label={t('pipelines:copy_job_id')}
          />
        }
        tabs={tabs}
        componentLabel={t('projects:add_project')}
        defaultTab={getDefaultTab()}
        trigger={trigger}
        width="72rem"
        disableTabs={tabs.length < 2}
        onOpenChange={setIsDrawerOpen}
      />
    </>
  );
};
